import { useState, useEffect, useCallback } from "react";

/**
 * Breakpoints aligned with Tailwind CSS defaults + project custom breakpoints.
 * sm: 640px, md: 768px, lg: 1024px, xl: 1280px, 2xl: 1536px, 3xl: 1600px
 */
const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
  "3xl": 1600,
};

/**
 * Coarse device class, for when you want to switch on one value rather than
 * combine several booleans. Ordered smallest to largest.
 */
export const DEVICE = {
  MOBILE: "mobile",
  TABLET: "tablet",
  LAPTOP: "laptop",
  DESKTOP: "desktop",
};

function getDeviceClass(width) {
  if (width < BREAKPOINTS.sm) return DEVICE.MOBILE; // < 640px
  if (width < BREAKPOINTS.lg) return DEVICE.TABLET; // 640 - 1023px
  if (width < BREAKPOINTS["3xl"]) return DEVICE.LAPTOP; // 1024 - 1599px
  return DEVICE.DESKTOP; // >= 1600px
}

function getDeviceInfo(width) {
  return {
    width,
    device: getDeviceClass(width),
    isMobile: width < BREAKPOINTS.sm, // < 640px
    isTablet: width >= BREAKPOINTS.sm && width < BREAKPOINTS.lg, // 640px - 1023px
    isDesktop: width >= BREAKPOINTS.lg, // >= 1024px
    isBigScreen: width >= BREAKPOINTS["3xl"], // >= 1600px
    isSmUp: width >= BREAKPOINTS.sm, // >= 640px
    isMdUp: width >= BREAKPOINTS.md, // >= 768px
    isLgUp: width >= BREAKPOINTS.lg, // >= 1024px
    isXlUp: width >= BREAKPOINTS.xl, // >= 1280px
    is2xlUp: width >= BREAKPOINTS["2xl"], // >= 1536px
    is3xlUp: width >= BREAKPOINTS["3xl"], // >= 1600px
  };
}

/**
 * Hook that provides device/viewport information with debounced resize handling.
 * Use this instead of inline `window.innerWidth` checks for consistent breakpoints.
 *
 * @example
 * const { isMobile, isTablet, isDesktop } = useDevice();
 * <Drawer width={isMobile ? "100%" : isTablet ? "80%" : "40%"} />
 */
export function useDevice() {
  // Guarded for SSR: gatsby build renders components without a window. Falls
  // back to the largest class so the desktop layout is what gets pre-rendered.
  const [device, setDevice] = useState(() =>
    getDeviceInfo(typeof window === "undefined" ? BREAKPOINTS["3xl"] : window.innerWidth)
  );

  const handleResize = useCallback(() => {
    setDevice(getDeviceInfo(window.innerWidth));
  }, []);

  useEffect(() => {
    let timeoutId;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 150);
    };

    window.addEventListener("resize", debouncedResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", debouncedResize);
    };
  }, [handleResize]);

  return device;
}

export { BREAKPOINTS };
